import React, { useEffect, useState } from "react";
import axios from "axios";
import Ornament from "@/components/Ornament";
import Reveal from "@/components/Reveal";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  guest_names: "",
  email: "",
  attendance: "accept",
  dietary: "",
  song: "",
  message: "",
  website: "", // honeypot
};

const RSVP = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [messages, setMessages] = useState([]);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    axios
      .get(`${API}/rsvp/messages`)
      .then((r) => setMessages(r.data || []))
      .catch(() => {});
  }, [done]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.guest_names.trim() || !form.email.trim()) {
      toast.error("Please share your name(s) and email.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/rsvp`, form);
      setDone(res.data);
      toast.success(
        form.attendance === "accept"
          ? "Thank you — we can't wait to celebrate with you."
          : "Thank you for letting us know — we'll miss you."
      );
      setForm(initialForm);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="rsvp" className="section rsvp" data-testid="section-rsvp">
      <Ornament />
      <Reveal as="h2" className="script-h2">
        RSVP
      </Reveal>
      <Reveal as="p" className="muted center">
        We kindly ask you to reply by <strong>31st August 2026</strong>.
      </Reveal>

      {done ? (
        <Reveal className="rsvp-success" data-testid="rsvp-success">
          <div className="rsvp-success-glyph">✽</div>
          <h3 className="script-h3">
            {done.attendance === "accept" ? "Joyfully received" : "Thank you"}
          </h3>
          <p>
            {done.attendance === "accept"
              ? `Thank you, ${done.guest_names}. We can't wait to celebrate with you on 21st November 2026.`
              : `We'll miss you, ${done.guest_names}. Thank you for letting us know.`}
          </p>
          <button className="btn ghost" onClick={() => setDone(null)} data-testid="rsvp-again">
            Submit another RSVP
          </button>
        </Reveal>
      ) : (
        <Reveal as="form" className="rsvp-form" onSubmit={submit} data-testid="rsvp-form">
          <label>
            Guest name(s)
            <input
              type="text"
              value={form.guest_names}
              onChange={change("guest_names")}
              placeholder="e.g. Alice & Guest"
              required
              data-testid="input-guest-names"
            />
          </label>

          <label>
            Email address
            <input
              type="email"
              value={form.email}
              onChange={change("email")}
              placeholder="you@example.com"
              required
              data-testid="input-email"
            />
          </label>

          <fieldset className="radio-row" aria-label="Attendance">
            <legend>Will you be joining us?</legend>
            <label className={`radio ${form.attendance === "accept" ? "checked" : ""}`}>
              <input
                type="radio"
                name="attendance"
                value="accept"
                checked={form.attendance === "accept"}
                onChange={change("attendance")}
                data-testid="radio-accept"
              />
              Joyfully accept
            </label>
            <label className={`radio ${form.attendance === "decline" ? "checked" : ""}`}>
              <input
                type="radio"
                name="attendance"
                value="decline"
                checked={form.attendance === "decline"}
                onChange={change("attendance")}
                data-testid="radio-decline"
              />
              Regretfully decline
            </label>
          </fieldset>

          <label>
            Dietary requirements
            <input
              type="text"
              value={form.dietary}
              onChange={change("dietary")}
              placeholder="Any allergies or preferences"
              data-testid="input-dietary"
            />
          </label>

          <label>
            A song that will get you dancing
            <input
              type="text"
              value={form.song}
              onChange={change("song")}
              placeholder="Artist — Song title"
              data-testid="input-song"
            />
          </label>

          <label>
            A message for the happy couple
            <textarea
              value={form.message}
              onChange={change("message")}
              rows={4}
              placeholder="Anything you'd like to say ✽"
              data-testid="input-message"
            />
          </label>

          {/* Honeypot */}
          <label className="hp" aria-hidden="true">
            Website
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={change("website")}
            />
          </label>

          <button className="btn primary block" type="submit" disabled={submitting} data-testid="btn-submit-rsvp">
            {submitting ? "Sending…" : "Send RSVP"}
          </button>
        </Reveal>
      )}

      {messages.length > 0 && (
        <div className="wishes">
          <Reveal as="h3" className="small-caps center">
            Kind words from friends
          </Reveal>
          <div className="wishes-grid">
            {messages.map((m) => (
              <Reveal key={m.id} className="wish">
                <p>“{m.message}”</p>
                <span className="wish-by">— {m.guest_names}</span>
              </Reveal>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default RSVP;
